/**
 * useCustomerAuth — slice-scope hook mirroring `apps/web-staff/src/lib/auth/useAuth.ts`.
 *
 * Reads the customer JWT from localStorage, exposes derived claims, and emits
 * a custom `upmp:customer-token-changed` event so multiple components can
 * react to login/logout in lockstep.
 */
import { useCallback, useEffect, useState } from 'react';

import { decodeCustomerClaims, type CustomerClaims } from './jwt';
import {
  clearCustomerToken,
  getCustomerToken,
  setCustomerToken as writeCustomerToken,
} from './tokenStore';

const TOKEN_CHANGED_EVENT = 'upmp:customer-token-changed';

interface UseCustomerAuthResult {
  readonly token: string | null;
  readonly claims: CustomerClaims | null;
  readonly isAuthenticated: boolean;
  readonly setToken: (token: string) => void;
  readonly logout: () => void;
}

export function useCustomerAuth(): UseCustomerAuthResult {
  const [token, setTokenState] = useState<string | null>(() => getCustomerToken());

  useEffect(() => {
    const onChange = (): void => {
      setTokenState(getCustomerToken());
    };
    window.addEventListener(TOKEN_CHANGED_EVENT, onChange);
    window.addEventListener('storage', onChange);
    return () => {
      window.removeEventListener(TOKEN_CHANGED_EVENT, onChange);
      window.removeEventListener('storage', onChange);
    };
  }, []);

  const setToken = useCallback((next: string): void => {
    writeCustomerToken(next);
    window.dispatchEvent(new Event(TOKEN_CHANGED_EVENT));
    setTokenState(next);
  }, []);

  const logout = useCallback((): void => {
    clearCustomerToken();
    window.dispatchEvent(new Event(TOKEN_CHANGED_EVENT));
    setTokenState(null);
  }, []);

  const claims = decodeCustomerClaims(token);

  return {
    token,
    claims,
    // A token is "authenticated" only if it carries a usable customerId
    // claim — a randomly typed string in the dev paste-box should NOT pass
    // the auth gate.
    isAuthenticated: token !== null && claims !== null,
    setToken,
    logout,
  };
}

export { TOKEN_CHANGED_EVENT as CUSTOMER_TOKEN_CHANGED_EVENT };
